import { useEffect, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSubAccounts, isAuthenticated } from 'actions/user';
import Render from 'components/render';
import { MARGIN_TYPES } from 'constants/constants';
import { getUserAccountNameById } from 'helpers/utils';
import { userSelector } from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { AngleUp } from 'styles/assets/icons';
import Box from 'UIKit/Box';

import MarginModeDropdown from './MarginModeDropdown';
import classes from './styles.module.scss';

interface MarginModeSelectionMobileProps {
  showFullHeader: boolean;
}

const MarginModeSelectionDesktop = () => {
  const { t } = useTranslation(['headerFooter']);
  const user = useSelector(userSelector);
  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;

  const { id } = user;
  const accountName = getUserAccountNameById(id, user);

  return (
    <Box className={classes.container}>
      <Box className={classes.wrapper}>
        <Box className={classes.marginModeSelector}>
          <Box className={classes.labelContainer}>
            <Box className={classes.type}>{t('headerFooter:marginMode.account')}</Box>
            <Box className={classes.type}>{t('headerFooter:marginMode.margin')}</Box>
          </Box>
          <Box className={classes.labelContainer}>
            <Box className={classes.value}>{accountName}</Box>
            <Box className={classes.value}>{marginType}</Box>
          </Box>
        </Box>
        <Box className={classes.iconWrapper}>
          <AngleUp />
        </Box>

        <MarginModeDropdown showDropdown />
      </Box>
    </Box>
  );
};

const MarginModeSelectionMobile = ({
  showFullHeader,
}: MarginModeSelectionMobileProps) => {
  const [showDropdown, setShowdropdown] = useState(false);
  const user = useSelector(userSelector);
  const appDispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(reduxState => isAuthenticated(reduxState?.user));

  useEffect(() => {
    if (isLoggedIn) {
      appDispatch(getSubAccounts());
    }
  }, [appDispatch, isLoggedIn]);

  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;

  const { id } = user;
  const accountName = getUserAccountNameById(id, user);

  const handleToggleDropdown = () => {
    setShowdropdown(prev => !prev);
  };

  const handleOverlayClick = e => {
    e.stopPropagation();
    handleToggleDropdown();
  };

  return (
    <Box renderWhen={!showFullHeader} className={classes.mobileWrapper}>
      <Box
        className={classes.container}
        data-testid="MarginModeSelectionMobile"
        onClick={handleToggleDropdown}
      >
        <Box className={classes.values}>
          <Box className={classes.account}>{accountName}</Box>
          <Box className={classes.marginType}>{marginType}</Box>
        </Box>
        <Box className={cx(classes.toggleIcon, { [classes.expanded]: showDropdown })}>
          <AngleUp />
        </Box>
      </Box>
      <MarginModeDropdown showDropdown={showDropdown} />
      <Render when={showDropdown}>
        <Box
          data-testid="MarginModeDropdownOverlay"
          className={classes.overlay}
          onClick={handleOverlayClick}
        />
      </Render>
    </Box>
  );
};

export { MarginModeSelectionDesktop, MarginModeSelectionMobile };
